import React from 'react';
import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Paper,
  Divider,
  Stack,
  Alert,
  Button,
  alpha,
  Grid
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  ArrowBack as ArrowBackIcon,
  Security as SecurityIcon,
  Shield as ShieldIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  Storage as StorageIcon,
  Share as ShareIcon
} from '@mui/icons-material';

import civiqLogo from '../img/civiq.jpg';

const PrivacyPolicy = ({ onNavigate }) => {
  const sections = [
    {
      title: "Information We Collect",
      icon: <StorageIcon />,
      content: [
        {
          subtitle: "Personal Information",
          text: "When you create an account, we collect your name, email address, and password. For premium features, we may collect billing information including address and payment details."
        },
        {
          subtitle: "Usage Data",
          text: "We automatically collect information about how you use CivIQ, including pages visited, features used, time spent on the platform, and your IP address."
        },
        {
          subtitle: "Device Information",
          text: "We collect information about the device you use to access CivIQ, including device type, operating system, browser type, and unique device identifiers."
        }
      ]
    },
    {
      title: "How We Use Your Information",
      icon: <VisibilityIcon />,
      content: [
        {
          subtitle: "Service Provision",
          text: "We use your information to provide, maintain, and improve CivIQ's services, including personalized political insights and constituency analysis."
        },
        {
          subtitle: "Communication",
          text: "We may send you important updates about your account, service changes, and relevant political news if you've opted in to our newsletter."
        },
        {
          subtitle: "Analytics",
          text: "We analyze usage patterns to improve our services, develop new features, and ensure platform security and performance."
        }
      ]
    },
    {
      title: "Information Sharing",
      icon: <ShareIcon />,
      content: [
        {
          subtitle: "Public Data",
          text: "CivIQ aggregates publicly available political and electoral data. This data is not linked to your personal information and remains anonymized."
        },
        {
          subtitle: "Service Providers",
          text: "We may share your information with trusted third-party service providers who help us operate our platform, such as cloud hosting, payment processing, and analytics services."
        },
        {
          subtitle: "Legal Requirements",
          text: "We may disclose your information if required by law, regulation, legal process, or governmental request."
        }
      ]
    },
    {
      title: "Data Security",
      icon: <LockIcon />,
      content: [
        {
          subtitle: "Encryption",
          text: "All data transmitted between your device and our servers is encrypted using industry-standard TLS encryption."
        },
        {
          subtitle: "Access Controls",
          text: "We implement strict access controls and authentication mechanisms to ensure only authorized personnel can access your data."
        },
        {
          subtitle: "Data Retention",
          text: "We retain your personal information only as long as necessary to provide our services or as required by applicable laws."
        }
      ]
    }
  ];

  const principles = [
    {
      icon: <SecurityIcon />,
      title: "Privacy by Design",
      description: "We build privacy protection into our systems from the ground up"
    },
    {
      icon: <ShieldIcon />,
      title: "Data Minimization",
      description: "We only collect data that's necessary for our services"
    },
    {
      icon: <LockIcon />,
      title: "Secure Storage",
      description: "Your data is stored securely with enterprise-grade protection"
    },
    {
      icon: <VisibilityIcon />,
      title: "Transparency",
      description: "We're clear about what data we collect and how we use it"
    }
  ];

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <AppBar position="static" sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)' }}>
        <Toolbar>
          <IconButton 
            edge="start" 
            color="inherit" 
            onClick={() => onNavigate('landing')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              src={civiqLogo}
              alt="CivIQ Logo"
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                mr: 1,
                border: '1px solid rgba(255,255,255,0.3)'
              }}
            />
            {/* <AnalyticsIcon sx={{ mr: 1 }} /> */}
            <Typography variant="h6" fontWeight="bold">
              Privacy Policy
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box sx={{ py: 8, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Typography variant="h2" fontWeight="bold" gutterBottom>
              Your Privacy
            </Typography>
            <Typography variant="h2" fontWeight="bold" gutterBottom sx={{ 
              background: 'linear-gradient(45deg, #ffd700, #ffeb3b)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Matters to Us
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: 600, mx: 'auto', mt: 3, opacity: 0.9 }}>
              This Privacy Policy explains how CivIQ collects, uses, and protects your personal information 
              when you use our political intelligence platform.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Last Updated Notice */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body1">
            <strong>Last Updated:</strong> December 2024 • This policy is effective immediately and applies to all users of CivIQ services.
          </Typography>
        </Alert>
      </Container>

      {/* Privacy Principles */}
      <Container maxWidth="lg" sx={{ pb: 8 }}>
        <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
          Our Privacy Principles
        </Typography>
        <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
          The core principles that guide how we handle your data
        </Typography>
        
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 3 }}>
          {principles.map((principle, index) => (
            <Paper key={index} sx={{ 
              p: 3, 
              textAlign: 'center',
              height: '100%',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 3
              }
            }}>
              <Box sx={{
                width: 60,
                height: 60,
                borderRadius: 2,
                background: alpha('#1976d2', 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2
              }}>
                {React.cloneElement(principle.icon, { 
                  sx: { color: 'primary.main', fontSize: 32 } 
                })}
              </Box>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {principle.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {principle.description}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Container>

      {/* Main Content */}
      <Box sx={{ backgroundColor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
            Privacy Policy Details
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            Comprehensive information about our data practices
          </Typography>

          <Stack spacing={6}>
            {sections.map((section, index) => (
              <Paper key={index} sx={{ p: 4, border: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: alpha('#1976d2', 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}>
                    {React.cloneElement(section.icon, { 
                      sx: { color: 'primary.main', fontSize: 24 } 
                    })}
                  </Box>
                  <Typography variant="h4" fontWeight="bold">
                    {section.title}
                  </Typography>
                </Box>
                
                <Stack spacing={3}>
                  {section.content.map((item, itemIndex) => (
                    <Box key={itemIndex}>
                      <Typography variant="h6" fontWeight="600" gutterBottom color="primary.main">
                        {item.subtitle}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {item.text}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Your Rights Section */}
      <Box sx={{ py: 8, backgroundColor: '#f8fafc' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
            Your Rights & Controls
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            You have full control over your personal data
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 4 }}>
            {[
              {
                title: "Access Your Data",
                description: "Request a copy of all personal data we hold about you",
                action: "Download your data archive anytime from your account settings"
              },
              {
                title: "Update Information",
                description: "Correct or update your personal information",
                action: "Edit your profile and preferences in your account dashboard"
              },
              {
                title: "Delete Your Account",
                description: "Request complete deletion of your account and data",
                action: "Contact us or use the account deletion option in settings"
              },
              {
                title: "Data Portability",
                description: "Export your data in a machine-readable format",
                action: "Available for all account holders through data export tools"
              },
              {
                title: "Opt-out Communications",
                description: "Unsubscribe from marketing emails and notifications",
                action: "Use unsubscribe links or update preferences in your account"
              },
              {
                title: "Restrict Processing",
                description: "Limit how we process your personal information",
                action: "Contact our privacy team to discuss specific restrictions"
              }
            ].map((right, index) => (
              <Paper key={index} sx={{ p: 4, height: '100%' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="primary.main">
                  {right.title}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {right.description}
                </Typography>
                <Typography variant="body2" fontWeight="500" sx={{ 
                  color: 'success.main',
                  backgroundColor: alpha('#4caf50', 0.1),
                  p: 1,
                  borderRadius: 1
                }}>
                  {right.action}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Cookies & Tracking */}
      <Box sx={{ py: 8, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
            Cookies & Tracking
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            How we use cookies and similar technologies
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, 1fr)' }, gap: 4 }}>
            {[
              {
                title: "Essential Cookies",
                description: "Required for basic website functionality, authentication, and security. These cannot be disabled.",
                examples: ["Login session", "Security tokens", "User preferences"]
              },
              {
                title: "Analytics Cookies",
                description: "Help us understand how users interact with CivIQ to improve our services. You can opt-out anytime.",
                examples: ["Page views", "Feature usage", "Performance metrics"]
              },
              {
                title: "Marketing Cookies",
                description: "Used to show relevant content and track marketing campaign effectiveness. Always optional.",
                examples: ["Ad targeting", "Campaign tracking", "Social media integration"]
              }
            ].map((cookie, index) => (
              <Paper key={index} sx={{ p: 4, height: '100%', border: '1px solid #e0e0e0' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {cookie.title}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                  {cookie.description}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                  Examples:
                </Typography>
                <Stack spacing={0.5}>
                  {cookie.examples.map((example, exIndex) => (
                    <Typography key={exIndex} variant="body2" color="text.secondary">
                      • {example}
                    </Typography>
                  ))}
                </Stack>
              </Paper>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Contact & Updates */}
      <Box sx={{ py: 8, backgroundColor: '#f8fafc' }}>
        <Container maxWidth="md">
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Questions About Privacy?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.7 }}>
              If you have any questions about this Privacy Policy, how we handle your data, 
              or want to exercise your privacy rights, please don't hesitate to contact us.
            </Typography>
            
            <Stack spacing={2} alignItems="center">
              <Typography variant="body1">
                <strong>Privacy Team:</strong> satvik140802@gmail.com
              </Typography>
              <Typography variant="body1">
                <strong>General Contact:</strong> satvik140802@gmail.com
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We'll respond to all privacy-related inquiries within 48 hours
              </Typography>
            </Stack>
            
            <Divider sx={{ my: 4 }} />
            
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Policy Updates
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              We may update this Privacy Policy from time to time. When we do, we'll notify you via email 
              and post the updated policy on this page with a new "Last Updated" date.
            </Typography>
            
            <Button 
              variant="contained" 
              onClick={() => onNavigate('contact')}
              sx={{ px: 4, py: 1.5, fontWeight: 600 }}
            >
              Contact Privacy Team
            </Button>
          </Paper>
        </Container>
      </Box>

      {/* Legal Information */}
      <Box sx={{ py: 6, backgroundColor: 'white', borderTop: '1px solid #e0e0e0' }}>
        <Container maxWidth="lg">
          <Typography variant="h5" fontWeight="bold" textAlign="center" gutterBottom>
            Legal Framework
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mt: 3 }}>
            <Box textAlign="center">
              <Typography variant="h6" fontWeight="600" gutterBottom>
                GDPR Compliant
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Full compliance with European Union General Data Protection Regulation
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="h6" fontWeight="600" gutterBottom>
                CCPA Compliant
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Adherence to California Consumer Privacy Act requirements
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Indian IT Act
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Compliance with Indian Information Technology Act and Rules
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
      {/* Footer */}
      <Box sx={{ py: 6, backgroundColor: '#263238', color: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" mb={2}>
                <Box 
                  sx={{ 
                    width: 40, 
                    height: 40, 
                    // background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}
                >
                  <Box
              component="img"
              src={civiqLogo}
              alt="CivIQ Logo"
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                mr: 1,
                border: '1px solid rgba(255,255,255,0.3)'
              }}
            />
                </Box>
                <Typography variant="h5" fontWeight="bold">
                  CivIQ
                </Typography>
              </Box>
              <Typography variant="body2" color="rgba(255,255,255,0.7)" sx={{ maxWidth: 400 }}>
                AI-powered political analytics platform for India's elections. Making complex political data accessible and actionable.
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Platform
                  </Typography>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('features')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      Features
                    </Button>
                  </Box>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('pricing')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      Pricing
                    </Button>
                  </Box>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('api')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      API
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Company
                  </Typography>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('about')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      About
                    </Button>
                  </Box>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('contact')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      Contact
                    </Button>
                  </Box>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('privacy')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      Privacy Policy
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.2)' }} />
          
          <Box textAlign="center">
            <Typography variant="body2" color="rgba(255,255,255,0.7)">
              © 2025 CivIQ. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PrivacyPolicy;
